import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ALL_ROLES, DANGEROUS_ROLES } from '@tips-sso/shared';
import { PageHeader } from '../components/PageHeader.js';
import { Field, StatusBadge } from '../components/Field.js';
import { Loading, ErrorState, EmptyState } from '../components/States.js';
import { errorMessage } from '../api/client.js';
import {
  configureScim,
  emitScimEvent,
  fetchScimConnection,
  type ConfigureScimInput,
  type ScimEventInput,
  type ScimGroupRoleMapping,
} from '../api/endpoints.js';

const IDP_TYPES: ConfigureScimInput['idpType'][] = [
  'entra_id',
  'okta',
  'google_workspace',
  'onelogin',
  'other',
];
const SCOPES: ScimGroupRoleMapping['assignmentScope'][] = [
  'own_org',
  'downstream_customer_org',
  'reseller_managed_customer',
];

// SCIM mappings may never assign dangerous platform roles (mirrors processScimEvent).
const ASSIGNABLE_ROLES = ALL_ROLES.filter((r) => !DANGEROUS_ROLES.has(r));

export function ScimPage() {
  const [orgId, setOrgId] = useState('');
  const [activeOrgId, setActiveOrgId] = useState('');

  return (
    <div className="page">
      <PageHeader
        title="SCIM"
        subtitle="Configure a customer's SCIM connection and group→role mappings."
      />

      <section className="card">
        <h3 className="card__title">Select customer org</h3>
        <form
          className="filter-row"
          onSubmit={(e) => {
            e.preventDefault();
            setActiveOrgId(orgId.trim());
          }}
        >
          <Field label="Customer org ID">
            <input
              className="field__input"
              value={orgId}
              placeholder="organizations.id (UUID)"
              onChange={(e) => setOrgId(e.target.value)}
            />
          </Field>
          <button type="submit" className="btn btn--primary">
            Load
          </button>
        </form>
      </section>

      {activeOrgId ? (
        <ScimConfig customerOrgId={activeOrgId} />
      ) : (
        <EmptyState
          title="No customer selected"
          message="Enter a customer org ID above to view or configure its SCIM connection."
        />
      )}
    </div>
  );
}

function ScimConfig({ customerOrgId }: { customerOrgId: string }) {
  const qc = useQueryClient();
  const conn = useQuery({
    queryKey: ['scim', customerOrgId],
    queryFn: () => fetchScimConnection(customerOrgId),
  });

  const [form, setForm] = useState<ConfigureScimInput>({
    connectionType: 'tips_scim_facade',
    idpType: 'entra_id',
    groupRoleMappings: [],
  });

  const save = useMutation({
    mutationFn: () => configureScim(customerOrgId, form),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scim', customerOrgId] }),
  });

  function addMapping() {
    setForm((f) => ({
      ...f,
      groupRoleMappings: [
        ...f.groupRoleMappings,
        {
          externalGroupName: '',
          targetRoleKey: ASSIGNABLE_ROLES[0] ?? '',
          targetProjectKey: 'tipsdata',
          assignmentScope: 'own_org',
        },
      ],
    }));
  }
  function updateMapping(i: number, patch: Partial<ScimGroupRoleMapping>) {
    setForm((f) => ({
      ...f,
      groupRoleMappings: f.groupRoleMappings.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    }));
  }
  function removeMapping(i: number) {
    setForm((f) => ({
      ...f,
      groupRoleMappings: f.groupRoleMappings.filter((_, idx) => idx !== i),
    }));
  }

  if (conn.isLoading) return <Loading />;
  if (conn.isError) return <ErrorState error={conn.error} onRetry={() => void conn.refetch()} />;

  return (
    <>
      {conn.data && (
        <section className="card">
          <div className="card__title-row">
            <h3 className="card__title">Existing connection</h3>
            <StatusBadge status={conn.data.status} />
          </div>
          <dl className="kv">
            <dt>Type</dt>
            <dd>{conn.data.connection_type}</dd>
            <dt>IdP</dt>
            <dd>{conn.data.idp_type}</dd>
            <dt>SCIM base URL</dt>
            <dd>{conn.data.scim_base_url ?? '—'}</dd>
            <dt>Mappings</dt>
            <dd>{conn.data.mappings?.length ?? 0}</dd>
          </dl>
        </section>
      )}

      <section className="card">
        <h3 className="card__title">{conn.data ? 'Update connection' : 'Create connection'}</h3>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          {save.isError && <div className="form-error">{errorMessage(save.error)}</div>}
          {save.isSuccess && <div className="notice notice--info">Saved.</div>}

          <div className="panel-grid">
            <Field label="Connection type">
              <select
                className="field__select"
                value={form.connectionType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    connectionType: e.target.value as ConfigureScimInput['connectionType'],
                  })
                }
              >
                <option value="tips_scim_facade">tips_scim_facade</option>
                <option value="zitadel_scim">zitadel_scim</option>
              </select>
            </Field>
            <Field label="IdP type">
              <select
                className="field__select"
                value={form.idpType}
                onChange={(e) =>
                  setForm({ ...form, idpType: e.target.value as ConfigureScimInput['idpType'] })
                }
              >
                {IDP_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="SCIM base URL" hint="Optional">
              <input
                className="field__input"
                value={form.scimBaseUrl ?? ''}
                onChange={(e) => setForm({ ...form, scimBaseUrl: e.target.value || undefined })}
              />
            </Field>
            <Field label="External tenant ID" hint="Optional">
              <input
                className="field__input"
                value={form.externalTenantId ?? ''}
                onChange={(e) =>
                  setForm({ ...form, externalTenantId: e.target.value || undefined })
                }
              />
            </Field>
          </div>

          <div className="card__title-row">
            <h4 className="card__title">Group → role mappings</h4>
            <button type="button" className="btn btn--secondary btn--sm" onClick={addMapping}>
              Add mapping
            </button>
          </div>

          {form.groupRoleMappings.length === 0 && (
            <p className="muted small">No mappings yet. Dangerous platform roles cannot be mapped.</p>
          )}

          {form.groupRoleMappings.map((m, i) => (
            <div key={i} className="panel-grid" style={{ alignItems: 'end' }}>
              <Field label="External group name">
                <input
                  className="field__input"
                  value={m.externalGroupName}
                  required
                  onChange={(e) => updateMapping(i, { externalGroupName: e.target.value })}
                />
              </Field>
              <Field label="Target role">
                <select
                  className="field__select"
                  value={m.targetRoleKey}
                  onChange={(e) => updateMapping(i, { targetRoleKey: e.target.value })}
                >
                  {ASSIGNABLE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Project key">
                <input
                  className="field__input"
                  value={m.targetProjectKey}
                  onChange={(e) => updateMapping(i, { targetProjectKey: e.target.value })}
                />
              </Field>
              <Field label="Scope">
                <select
                  className="field__select"
                  value={m.assignmentScope}
                  onChange={(e) =>
                    updateMapping(i, {
                      assignmentScope: e.target.value as ScimGroupRoleMapping['assignmentScope'],
                    })
                  }
                >
                  {SCOPES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={() => removeMapping(i)}
              >
                Remove
              </button>
            </div>
          ))}

          <div className="form__actions">
            <button type="submit" className="btn btn--primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save connection'}
            </button>
          </div>
        </form>
      </section>

      {conn.data && <EmitEventCard scimConnectionId={conn.data.id} />}
    </>
  );
}

function EmitEventCard({ scimConnectionId }: { scimConnectionId: string }) {
  const [form, setForm] = useState<ScimEventInput>({
    scimConnectionId,
    eventType: 'user.add',
    email: '',
  });
  const mutation = useMutation({ mutationFn: () => emitScimEvent(form) });

  return (
    <section className="card">
      <h3 className="card__title">Replay / test SCIM event</h3>
      <form
        className="filter-row"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Event type">
          <select
            className="field__select"
            value={form.eventType}
            onChange={(e) =>
              setForm({ ...form, eventType: e.target.value as ScimEventInput['eventType'] })
            }
          >
            <option value="user.add">user.add</option>
            <option value="user.remove">user.remove</option>
            <option value="group.add">group.add</option>
            <option value="group.remove">group.remove</option>
          </select>
        </Field>
        <Field label="Email">
          <input
            className="field__input"
            value={form.email ?? ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <button type="submit" className="btn btn--secondary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Processing…' : 'Emit event'}
        </button>
      </form>
      {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
      {mutation.isSuccess && <div className="notice notice--info">Event processed.</div>}
    </section>
  );
}
